import React from 'react';
import type { AttentionItem, CurrentUserIdentity } from '../../domain/types';
import { Button } from '../../ui/common';
import { GitHubChecksSection } from './GitHubChecksSection';
import { GitHubReviewersSection } from './GitHubReviewersSection';
import { GitHubCommentsSection } from './GitHubCommentsSection';
import { GitHubAiReviewPanel } from './GitHubAiReviewPanel';
import type { GitHubAiReviewState } from '../../context/slices/useGitHubSlice';
import type { AiReviewPublishEvent } from '@zunelo/shared';
import { ProviderCommentComposer } from '../../ui/common/ProviderCommentComposer';
import type { ComposerMentionCandidate } from '../../ui/common/RichTextComposer';
import {
  githubMergeBoxBackground,
  githubMergeStatusColor,
  githubMergeStatusGlyph,
} from './githubCardVisuals';

interface GitHubCardExpandedProps {
  item: AttentionItem;
  isCurrentUserApproved: boolean;
  isReRunningChecks: boolean;
  totalChecksConfigured: number;
  currentUser?: CurrentUserIdentity;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
  onOpenMergeModal: (e: React.MouseEvent) => void;
  onApprovePR: (e: React.MouseEvent) => void;
  onToggleCommentState: (itemId: string, commentIndex: number) => void;
  onReRunChecks: (e: React.MouseEvent) => void;
  onToggleCheckRun: (itemId: string, checkIndex: number) => void;
  onOpenPr?: (e: React.MouseEvent) => void;
  /** One-click AI review: state + handlers come from the GitHub slice. */
  aiReview?: GitHubAiReviewState;
  onAiReview?: (e: React.MouseEvent) => void;
  onAiReviewRegenerate?: () => void;
  onAiReviewPublish?: (event: AiReviewPublishEvent) => void;
  onAiReviewDismiss?: () => void;
  onPostComment?: (markdown: string) => Promise<void>;
  onGenerateCommentDraft?: (hint: string) => Promise<string | null>;
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  /** PR briefing summary ("Resumen del ítem") — distinct from `aiReview`. */
  itemSummaryBusy?: boolean;
  onGenerateItemSummary?: () => void;
}

function RefChips({
  item,
  onNavigateToJira,
  isJiraRefResolved,
}: {
  item: AttentionItem;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
}): React.ReactElement | null {
  if (!item.refs || item.refs.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
      {item.refs.map((rf) => {
        const resolved = isJiraRefResolved ? isJiraRefResolved(rf.key) : true;
        return (
          <button
            type="button"
            key={rf.key}
            aria-label={resolved ? `Ver ${rf.key}` : `${rf.key}: no está cargado en Zunelo`}
            aria-disabled={!resolved}
            disabled={!resolved}
            title={resolved ? undefined : 'No está cargado en Zunelo'}
            onClick={(e) => {
              if (!resolved) return;
              e.stopPropagation();
              onNavigateToJira(rf.key);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 11px',
              borderRadius: 999,
              cursor: resolved ? 'pointer' : 'not-allowed',
              opacity: resolved ? 1 : 0.55,
              background: 'var(--z-accent-wash)',
              border: 'none',
            }}
          >
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--z-accent-ink)',
              }}
            >
              {rf.key}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--z-accent-copy)' }}>{rf.why}</span>
          </button>
        );
      })}
    </div>
  );
}

function MergePrimaryAction({
  item,
  onOpenMergeModal,
}: {
  item: AttentionItem;
  onOpenMergeModal: (e: React.MouseEvent) => void;
}): React.ReactElement {
  if (item.merged) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '4px 10px',
          borderRadius: 8,
          background: 'var(--z-ok-bg)',
          color: 'var(--z-ok-ink)',
          fontSize: 11.5,
          fontWeight: 700,
        }}
      >
        ✓ PR Mergeado en {item.base || 'main'}
      </span>
    );
  }
  return (
    <Button variant="primary" size="sm" onClick={onOpenMergeModal}>
      <span>Mergear PR</span>
    </Button>
  );
}

function MergeApprovalStatus({
  item,
  isCurrentUserApproved,
  onApprovePR,
}: {
  item: AttentionItem;
  isCurrentUserApproved: boolean;
  onApprovePR: (e: React.MouseEvent) => void;
}): React.ReactElement | null {
  if (item.merged) return null;
  if (isCurrentUserApproved) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 11.5,
          fontWeight: 600,
          color: 'var(--z-ok-ink)',
        }}
      >
        ✓ Aprobado por ti
      </span>
    );
  }
  return (
    <Button variant="ghost" size="sm" onClick={onApprovePR}>
      <span>Aprobar PR</span>
    </Button>
  );
}

function MergeActionBar({
  item,
  isCurrentUserApproved,
  onOpenMergeModal,
  onApprovePR,
  onOpenPr,
  onAiReview,
  aiReviewBusy,
}: {
  item: AttentionItem;
  isCurrentUserApproved: boolean;
  onOpenMergeModal: (e: React.MouseEvent) => void;
  onApprovePR: (e: React.MouseEvent) => void;
  onOpenPr?: (e: React.MouseEvent) => void;
  onAiReview?: (e: React.MouseEvent) => void;
  aiReviewBusy?: boolean;
}): React.ReactElement {
  return (
    // Ordered along the decision, not by visual weight: review, then
    // approve, then open, and only then the one irreversible action. Leading
    // with "Mergear PR" put the destructive step under the cursor first and
    // pushed the review — the thing you do to decide — into third place.
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {onAiReview && !item.merged ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`github-ai-review-button-${item.id}`}
          disabled={aiReviewBusy}
          onClick={onAiReview}
        >
          <span>{aiReviewBusy ? 'Revisando…' : '✦ Revisar con IA'}</span>
        </Button>
      ) : null}
      <MergeApprovalStatus
        item={item}
        isCurrentUserApproved={isCurrentUserApproved}
        onApprovePR={onApprovePR}
      />
      {onOpenPr ? (
        <Button
          variant="ghost"
          size="sm"
          data-testid={`github-open-pr-${item.id}`}
          onClick={onOpenPr}
        >
          <span>Entrar al PR</span>
        </Button>
      ) : null}
      <MergePrimaryAction item={item} onOpenMergeModal={onOpenMergeModal} />
    </div>
  );
}

export const GitHubCardExpanded: React.FC<GitHubCardExpandedProps> = ({
  item,
  isCurrentUserApproved,
  isReRunningChecks,
  totalChecksConfigured,
  currentUser,
  onNavigateToJira,
  isJiraRefResolved,
  onOpenMergeModal,
  onApprovePR,
  onToggleCommentState,
  onReRunChecks,
  onToggleCheckRun,
  onOpenPr,
  aiReview,
  onAiReview,
  onAiReviewRegenerate,
  onAiReviewPublish,
  onAiReviewDismiss,
  onPostComment,
  onGenerateCommentDraft,
  onMentionCandidateUnavailable,
  itemSummaryBusy = false,
  onGenerateItemSummary,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      padding: '12px 13px',
      marginBottom: 12,
      borderRadius: 12,
      background: 'var(--z-fill)',
    }}
  >
    <RefChips
      item={item}
      onNavigateToJira={onNavigateToJira}
      isJiraRefResolved={isJiraRefResolved}
    />

    {onGenerateItemSummary ? (
      <div
        data-testid="github-item-summary"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 11,
          background: 'var(--z-accent-wash)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--z-accent-ink)' }}>
            Resumen del ítem
          </span>
          <button
            type="button"
            data-testid="github-item-summary-refresh"
            disabled={itemSummaryBusy}
            onClick={(e) => {
              e.stopPropagation();
              onGenerateItemSummary();
            }}
            style={{
              marginLeft: 'auto',
              height: 24,
              padding: '0 10px',
              borderRadius: 8,
              border: 'none',
              cursor: itemSummaryBusy ? 'wait' : 'pointer',
              fontSize: 11,
              fontWeight: 600,
              background: 'var(--z-accent-soft)',
              color: 'var(--z-accent-ink)',
              opacity: itemSummaryBusy ? 0.7 : 1,
            }}
          >
            {itemSummaryBusy ? 'Generando…' : 'Actualizar resumen'}
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--z-muted-3)' }}>
          {item.ai}
        </p>
      </div>
    ) : null}

    <div
      style={{
        padding: '13px 15px',
        borderRadius: 14,
        background: githubMergeBoxBackground(item),
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5, flexWrap: 'wrap' }}
      >
        <span
          style={{
            width: 15,
            height: 15,
            borderRadius: '50%',
            background: githubMergeStatusColor(item),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 800,
            fontSize: 10,
          }}
        >
          {githubMergeStatusGlyph(item)}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)', minWidth: 0 }}>
          {item.merged ? 'Mergeado con éxito' : item.mergeTitle}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            color: 'var(--z-dim)',
            flex: '1 1 auto',
            textAlign: 'right',
          }}
        >
          {item.head} → {item.base} · +{item.add} −{item.del}
        </span>
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.5, color: 'var(--z-muted-3)' }}>
        {item.merged ? 'Los cambios han sido incorporados a la rama principal.' : item.mergeText}
      </p>
      <MergeActionBar
        item={item}
        isCurrentUserApproved={isCurrentUserApproved}
        onOpenMergeModal={onOpenMergeModal}
        onApprovePR={onApprovePR}
        onOpenPr={onOpenPr}
        onAiReview={onAiReview}
        aiReviewBusy={aiReview?.status === 'loading'}
      />
    </div>

    {aiReview && onAiReviewRegenerate && onAiReviewPublish && onAiReviewDismiss ? (
      <GitHubAiReviewPanel
        itemId={item.id}
        state={aiReview}
        isAuthor={item.role === 'author'}
        onRegenerate={onAiReviewRegenerate}
        onPublish={onAiReviewPublish}
        onDismiss={onAiReviewDismiss}
      />
    ) : null}

    <GitHubReviewersSection
      reviewers={item.reviewers}
      isCurrentUserApproved={isCurrentUserApproved}
      merged={item.merged}
      currentUser={currentUser}
      onApprovePR={onApprovePR}
    />

    <GitHubCommentsSection
      itemId={item.id}
      comments={item.comments}
      onToggleCommentState={onToggleCommentState}
    />

    {onPostComment ? (
      <ProviderCommentComposer
        platform="github"
        data-testid="github-comment-composer"
        onSubmit={onPostComment}
        onGenerateDraft={onGenerateCommentDraft}
        onMentionCandidateUnavailable={onMentionCandidateUnavailable}
      />
    ) : null}

    <GitHubChecksSection
      itemId={item.id}
      checks={item.checks}
      files={item.files}
      isReRunningChecks={isReRunningChecks}
      totalChecksConfigured={totalChecksConfigured}
      onReRunChecks={onReRunChecks}
      onToggleCheckRun={onToggleCheckRun}
    />
  </div>
);
